export const LOCATION_SWITZERLAND = 'switzerland'
export const LOCATION_OUTSIDE_SWITZERLAND = 'outside_switzerland'

export const LOCATION_TYPES = [
	LOCATION_SWITZERLAND,
	LOCATION_OUTSIDE_SWITZERLAND
]

const isPresent = value => value !== null && value !== undefined && value !== ''

const toNumber = value => {
	if (
		!isPresent(value) ||
		(typeof value === 'string' && value.trim() === '')
	) {
		return null
	}

	const number = Number(value)

	return Number.isFinite(number) ? number : null
}

export const isOutsideSwitzerland = value =>
	value === LOCATION_OUTSIDE_SWITZERLAND

export const isSwitzerland = value => value === LOCATION_SWITZERLAND

export const normalizeProvinceList = (record = {}, provinceOptions = []) => {
	if (Array.isArray(record?.provinces) && record.provinces.length > 0) {
		return record.provinces.filter(Boolean)
	}

	if (record?.province) {
		return [record.province]
	}

	if (isPresent(record?.province_id)) {
		const provinceId = toNumber(record.province_id)
		const option = provinceOptions.find(
			province => province.id == provinceId
		)

		return option ? [option] : [{ id: provinceId }]
	}

	return []
}

export const effectiveLocationType = (
	record = {},
	{ defaultType = LOCATION_SWITZERLAND } = {}
) => {
	if (LOCATION_TYPES.includes(record?.location_type)) {
		return record.location_type
	}

	if (isPresent(record?.outside_location)) {
		return LOCATION_OUTSIDE_SWITZERLAND
	}

	if (
		(Array.isArray(record?.provinces) && record.provinces.length > 0) ||
		record?.province ||
		isPresent(record?.province_id)
	) {
		return LOCATION_SWITZERLAND
	}

	return defaultType
}

export const normalizeLocationForm = (
	record = {},
	provinceOptions = [],
	{ singleProvince = false, structured = false } = {}
) => {
	const locationType = effectiveLocationType(record)
	const provinces = isOutsideSwitzerland(locationType)
		? []
		: normalizeProvinceList(record, provinceOptions)
	const selectedProvinces = singleProvince ? provinces.slice(0, 1) : provinces

	return {
		location_type: locationType,
		...(structured
			? {
					country_id:
						record?.country_id || record?.country?.id || null,
					region: record?.region || '',
					zip: String(record?.zip || ''),
					town_id: record?.town_id || record?.town?.id || null
			  }
			: {}),
		city:
			isSwitzerland(locationType) || structured
				? record?.city || ''
				: null,
		outside_location: isOutsideSwitzerland(locationType)
			? record?.outside_location || ''
			: null,
		provinces: selectedProvinces,
		province_id: selectedProvinces[0]?.id || null
	}
}

export const serializeProvinces = provinces => {
	if (!Array.isArray(provinces)) {
		return []
	}

	return provinces
		.map(province => {
			const id = province?.id ?? province?.value ?? province
			const normalizedId = toNumber(id)

			return normalizedId
				? {
						id: normalizedId,
						attributes: {}
				  }
				: null
		})
		.filter(Boolean)
}

export const serializeLocationPayload = (
	record,
	{ structured = false } = {}
) => {
	const locationType = effectiveLocationType(record)
	const outsideLocation = (record?.outside_location || '').trim()
	const provinces = normalizeProvinceList(record)
	const firstProvinceId = provinces[0]?.id || record?.province_id || null

	if (isOutsideSwitzerland(locationType)) {
		if (structured) {
			return {
				location_type: LOCATION_OUTSIDE_SWITZERLAND,
				country_id: toNumber(record?.country_id),
				region: String(record?.region || '').trim(),
				city: String(record?.city || '').trim(),
				zip: String(record?.zip || '').trim(),
				town_id: null,
				provinces: [],
				province_id: null
			}
		}

		return {
			location_type: LOCATION_OUTSIDE_SWITZERLAND,
			city: null,
			outside_location: outsideLocation,
			provinces: [],
			province_id: null
		}
	}

	return {
		location_type: LOCATION_SWITZERLAND,
		...(structured
			? {
					country_id: toNumber(record?.country_id),
					region: null,
					zip: String(record?.zip || '').trim(),
					town_id: toNumber(record?.town_id)
			  }
			: {}),
		city: String(record?.city || '').trim(),
		outside_location: null,
		provinces: serializeProvinces(provinces),
		province_id: firstProvinceId ? toNumber(firstProvinceId) : null
	}
}

export const provinceName = (province, localeColumn = 'name') => {
	if (!province) {
		return ''
	}

	return (
		province[localeColumn] ||
		province.name ||
		province.name_en ||
		province.name_de ||
		''
	)
}

const provincesForDisplay = record => {
	const provinces = normalizeProvinceList(record)
	const primary =
		provinces.find(province => province.id == record?.town?.province_id) ||
		provinces.find(province => province.id == record?.province_id)

	return primary
		? [primary, ...provinces.filter(province => province !== primary)]
		: provinces
}

export const getCompactLocationDisplay = (
	record = {},
	{
		localeColumn = 'name',
		visibleCount = 1,
		fallback = '',
		countryLabel = ''
	} = {}
) => {
	const locationType = effectiveLocationType(record, { defaultType: null })

	if (isOutsideSwitzerland(locationType)) {
		const label = outsideSwitzerlandLabel(record, fallback, localeColumn)

		return {
			visible: label
				? [
						{
							id: LOCATION_OUTSIDE_SWITZERLAND,
							label
						}
				  ]
				: [],
			hiddenCount: 0,
			items: [],
			label,
			tooltip: ''
		}
	}

	const city = String(record?.city || '').trim()
	const locations = provincesForDisplay(record)
		.map(province => ({
			id: province.id,
			label: provinceName(province, localeColumn)
		}))
		.filter(location => location.label)
	const displayLocations = locations.map((location, index) => ({
		...location,
		label:
			index === 0 && city
				? [city, location.label, countryLabel]
						.filter(Boolean)
						.join(', ')
				: location.label
	}))
	const visible = displayLocations.slice(0, visibleCount)
	const hidden = displayLocations.slice(visible.length)
	const items = hidden.map(location => location.label)
	const labels = displayLocations.map(location => location.label)

	return {
		visible,
		hiddenCount: Math.max(locations.length - visible.length, 0),
		items,
		label: labels.length > 0 ? labels.join(', ') : fallback,
		tooltip: items.join(', ')
	}
}

export const formatLocation = (
	record = {},
	{ localeColumn = 'name', fallback = '', countryLabel = '' } = {}
) => {
	const locationType = effectiveLocationType(record, { defaultType: null })

	if (isOutsideSwitzerland(locationType)) {
		return outsideSwitzerlandLabel(record, fallback, localeColumn)
	}

	const provinceLabels = provincesForDisplay(record)
		.map(province => provinceName(province, localeColumn))
		.filter(Boolean)
	const city = String(record?.city || '').trim()

	if (city && provinceLabels.length > 0) {
		return [city, provinceLabels[0], countryLabel]
			.filter(Boolean)
			.join(', ')
	}

	return provinceLabels.length > 0 ? provinceLabels.join(', ') : fallback
}

const outsideSwitzerlandLabel = (
	record,
	fallback = '',
	localeColumn = 'name'
) => {
	const legacyLabel = String(record?.outside_location || '').trim()
	const structuredCountry =
		record?.country && typeof record.country === 'object'
			? record.country
			: null
	const structuredLabel = [
		record?.city,
		record?.region,
		structuredCountry?.[localeColumn] ||
			structuredCountry?.name ||
			structuredCountry?.name_en
	]
		.map(value => String(value || '').trim())
		.filter(Boolean)
		.join(', ')
	const hasStructuredContext = Boolean(
		isPresent(record?.country_id) ||
			structuredCountry ||
			String(record?.region || '').trim() ||
			String(record?.zip || '').trim()
	)

	return (
		(hasStructuredContext ? structuredLabel : legacyLabel) ||
		structuredLabel ||
		legacyLabel ||
		fallback
	)
}

const normalizeOutsideLocationValue = value =>
	String(value || '')
		.split(',')
		.map(part => part.trim().toLowerCase())
		.filter(Boolean)
		.join(', ')

const hasStructuredOutsideLocation = record =>
	Boolean(
		isPresent(record?.country_id) ||
			(record?.country && typeof record.country === 'object') ||
			String(record?.region || '').trim() ||
			String(record?.zip || '').trim()
	)

const structuredCountryKeys = record => {
	const country =
		record?.country && typeof record.country === 'object'
			? record.country
			: {}
	const countryId = record?.country_id || country.id

	return [
		countryId ? `id:${countryId}` : '',
		country.code ? `code:${String(country.code).toUpperCase()}` : '',
		...['name', 'name_en', 'name_de']
			.map(field => normalizeOutsideLocationValue(country[field]))
			.filter(Boolean)
			.map(name => `name:${name}`)
	].filter(Boolean)
}

const structuredOutsideLocationVariants = record => {
	const city = String(record?.city || '').trim()
	const region = String(record?.region || '').trim()
	const country =
		record?.country && typeof record.country === 'object'
			? record.country
			: {}
	const countryLabels = [country.name, country.name_en, country.name_de, country.code]
		.map(normalizeOutsideLocationValue)
		.filter(Boolean)
	const variants = new Set()

	countryLabels.forEach(countryLabel => {
		const addVariant = parts => {
			const uniqueParts = parts.filter(Boolean).filter(
				(part, index, values) =>
					index === 0 ||
					normalizeOutsideLocationValue(part) !==
						normalizeOutsideLocationValue(values[index - 1])
			)
			const value = normalizeOutsideLocationValue(uniqueParts.join(', '))

			if (value) variants.add(value)
		}

		addVariant([city, region, countryLabel])
		addVariant([city, countryLabel])
		addVariant([region, countryLabel])
		addVariant([countryLabel])
	})

	return variants
}

const structuredOutsideLocationsOverlap = (left, right) => {
	const leftCountries = structuredCountryKeys(left)
	const rightCountries = structuredCountryKeys(right)
	const countriesOverlap = leftCountries.some(country =>
		rightCountries.includes(country)
	)

	if (!countriesOverlap) return false

	return ['city', 'region'].every(field => {
		const leftValue = normalizeOutsideLocationValue(left?.[field])
		const rightValue = normalizeOutsideLocationValue(right?.[field])

		return !leftValue || !rightValue || leftValue === rightValue
	})
}

export const locationsOverlap = (left = {}, right = {}) => {
	const leftType = effectiveLocationType(left, { defaultType: null })
	const rightType = effectiveLocationType(right, { defaultType: null })

	if (!leftType || !rightType) {
		return false
	}

	if (isOutsideSwitzerland(leftType) || isOutsideSwitzerland(rightType)) {
		if (
			!isOutsideSwitzerland(leftType) ||
			!isOutsideSwitzerland(rightType)
		) {
			return false
		}

		const leftStructured = hasStructuredOutsideLocation(left)
		const rightStructured = hasStructuredOutsideLocation(right)

		if (leftStructured && rightStructured) {
			return structuredOutsideLocationsOverlap(left, right)
		}

		if (leftStructured || rightStructured) {
			const structured = leftStructured ? left : right
			const legacy = leftStructured ? right : left
			const legacyLabel = normalizeOutsideLocationValue(
				legacy?.outside_location
			)

			return (
				legacyLabel !== '' &&
				structuredOutsideLocationVariants(structured).has(legacyLabel)
			)
		}

		return (
			normalizeOutsideLocationValue(left.outside_location) ===
			normalizeOutsideLocationValue(right.outside_location)
		)
	}

	const leftIds = normalizeProvinceList(left)
		.map(province => province.id)
		.filter(isPresent)
		.map(String)
	const rightIds = normalizeProvinceList(right)
		.map(province => province.id)
		.filter(isPresent)
		.map(String)

	return leftIds.some(id => rightIds.includes(id))
}
